import { useCallback, useMemo, useState } from "react";
import { CATEGORIES, BROWSABLE_CATEGORIES } from "@oshimap/config";
import { categoryColors } from "@oshimap/design-tokens";
import { normalizeCategoryCode } from "@oshimap/domain";
import { ep } from "../../lib/api";
import { useQuery } from "../../lib/use-query";
import { Button, EmptyState, ErrorState, Skeleton, CategoryIcon } from "../../components/ui";
import { play, sparkle } from "../../lib/motion";
import "./oshi.css";
import { queryKeys } from "@oshimap/query";

/**
 * F-23 / FR-SAVE-05. Legacy `user_interests` 를 그대로 쓴다.
 * 실측: 관심 대상은 tagId / categoryCode / region 중 최소 하나를 가진다.
 * 작품·캐릭터 단위 Oshi 는 태그로 표현된다 — 전용 테이블이 없다.
 */
export function OshiPane() {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const interests = useQuery(queryKeys.interests(), (signal) => ep.listInterests({ signal }));
  const tags = useQuery(queryKeys.tags(), (signal) => ep.listTags({ signal }));
  const recs = useQuery([...queryKeys.recommendations(), interests.state.status], (signal) => ep.recommendations({ signal }));

  const byTag = useMemo(() => {
    const m = new Map<string, string>();
    if (tags.state.status === "success") for (const t of tags.state.data) m.set(t.id, t.name);
    return m;
  }, [tags.state]);

  const activeCategories = useMemo(() => {
    if (interests.state.status !== "success") return new Set<string>();
    return new Set(interests.state.data.map((i) => i.categoryCode).filter(Boolean) as string[]);
  }, [interests.state]);

  /**
   * 서버 점수 = 카테고리 3 + 태그 2 + 지역 1 + 최근 확인 1 (실측: interests/routes.py).
   * 숫자를 그대로 보여주면 읽는 사람에게 아무 뜻이 없다. 내가 등록한 것과 맞춰
   * 설명할 수 있는 이유만 라벨로 바꾼다 — 추측한 이유는 붙이지 않는다.
   */
  const reasonFor = useCallback((categoryName: string, addressLine: string) => {
    if (interests.state.status !== "success") return null;
    const mine = interests.state.data;
    const catNames = new Set(mine.map((i) => i.categoryCode)
      .filter((code): code is string => Boolean(code))
      .map((code) => CATEGORIES[normalizeCategoryCode(code)].label));
    if (catNames.has(categoryName)) return "관심 카테고리";
    const region = mine.map((i) => i.region).find((r) => r && addressLine.startsWith(r));
    if (region) return "관심 지역";
    return null;
  }, [interests.state]);

  const toggleCategory = useCallback(async (code: string) => {
    if (interests.state.status !== "success") return;
    setBusy(code); setError(null);
    try {
      const existing = interests.state.data.find((i) => i.categoryCode === code);
      if (existing) await ep.deleteInterest(existing.id);
      else await ep.createInterest({ categoryCode: code });
      interests.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "저장하지 못했어요.");
    } finally { setBusy(null); }
  }, [interests]);

  if (interests.state.status === "loading" || interests.state.status === "idle") {
    return <div className="om-me__pad"><Skeleton lines={2} /></div>;
  }
  if (interests.state.status === "error") {
    return <div className="om-me__pad">
      <ErrorState title="최애 설정을 불러오지 못했어요" body={interests.state.error.message} onRetry={interests.reload} />
    </div>;
  }

  const list = interests.state.data;

  return (
    <div className="om-me__pad">
      <h2 className="om-section__title" style={{ padding: 0 }}>관심 카테고리</h2>
      <p className="om-note">등록하면 홈과 추천에 먼저 올라옵니다.</p>
      <div className="om-oshigrid">
        {BROWSABLE_CATEGORIES.map((c) => {
          const on = activeCategories.has(c.code);
          const col = categoryColors[c.code];
          return (
            <button key={c.code} className="om-oshicard" aria-pressed={on} disabled={busy === c.code}
              style={on ? { borderColor: col.fg, background: col.bg } : undefined}
              onClick={(e) => {
                play(e.currentTarget, "mo-pop");
                if (!on) sparkle(e.currentTarget, 4);
                void toggleCategory(c.code);
              }}>
              <span className="om-oshicard__ico"><CategoryIcon code={c.code} size={22} /></span>
              <b style={on ? { color: col.fg } : undefined}>{c.label}</b>
              <small>{on ? "등록됨" : "탭해서 등록"}</small>
            </button>
          );
        })}
      </div>
      {error ? <p className="om-note om-note--err" role="alert">{error}</p> : null}

      {list.some((i) => i.tagId) ? (
        <>
          <h2 className="om-section__title" style={{ padding: 0, marginTop: "var(--s-6)" }}>관심 태그</h2>
          <div className="om-chiprow">
            {list.filter((i) => i.tagId).map((i) => (
              <span key={i.id} className="om-oshitag">
                {byTag.get(i.tagId!) ?? i.tagId}
                <button aria-label={`${byTag.get(i.tagId!) ?? "태그"} 삭제`}
                  onClick={() => {
                    /* 실패하면 칩이 그대로 남습니다 — 지워진 척하지 않습니다. */
                    void ep.deleteInterest(i.id)
                      .then(() => interests.reload())
                      .catch((e: unknown) => setError(e instanceof Error ? e.message : "지우지 못했어요."));
                  }}>×</button>
              </span>
            ))}
          </div>
        </>
      ) : null}

      <h2 className="om-section__title" style={{ padding: 0, marginTop: "var(--s-6)" }}>추천</h2>
      {recs.state.status === "loading" ? <Skeleton lines={2} />
      : recs.state.status === "error" ? <p className="om-note">추천을 불러오지 못했어요.</p>
      : recs.state.status === "success" && recs.state.data.length > 0 ? (
        <>
          <p className="om-note">규칙 기반 추천입니다. AI 를 쓰지 않아요.</p>
          <ul className="om-rowlist">
            {recs.state.data.slice(0, 6).map((r) => {
              const why = reasonFor(r.category, r.addressLine);
              return (
                <li key={r.id}>
                  <span>
                    <b>{r.name}</b>
                    <small>{r.category} · {r.addressLine}</small>
                  </span>
                  {why ? <span className="om-why">{why}</span> : null}
                </li>
              );
            })}
          </ul>
        </>
      ) : (
        <EmptyState level={3} title="추천할 곳이 아직 없어요"
          body="관심 카테고리를 등록하면 맞춰서 보여드릴게요." />
      )}

      {list.length === 0 ? (
        <div style={{ marginTop: "var(--s-5)" }}>
          <EmptyState level={3} title="아직 등록한 최애가 없어요"
            body="관심 카테고리를 하나만 골라도 홈이 달라집니다."
            action={<Button variant="secondary" size="sm" onClick={() => void toggleCategory("POPUP_STORE")}>팝업스토어부터</Button>} />
        </div>
      ) : null}
    </div>
  );
}
